
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parseISO } from "date-fns"
import { CalendarIcon, ChevronLeft, ChevronRight, Plus } from "lucide-react"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useTrainer } from "@/contexts/trainer-context"
import { AddAppointmentDialog } from "@/components/trainer-view/add-appointment-dialog"
import { AppointmentDetails } from "@/components/trainer-view/appointment-details"
import { mockUsers } from "@/lib/mock-data"

export function TrainerCalendar() {
  const { t } = useTranslation()
  const { appointments, getTrainerAppointments } = useTrainer()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [isAddAppointmentOpen, setIsAddAppointmentOpen] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState<string | null>(null)

  // Mock trainer ID
  const trainerId = "1"
  const trainerAppointments = getTrainerAppointments(trainerId)

  // Get the start and end of the current week
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }) // Start on Monday
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 })
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd })

  // Get appointments for the selected date
  const selectedDateAppointments = trainerAppointments.filter((appointment) =>
    isSameDay(parseISO(appointment.date), selectedDate),
  )

  // Navigate to previous week
  const previousWeek = () => {
    setCurrentDate(addDays(currentDate, -7))
  }

  // Navigate to next week
  const nextWeek = () => {
    setCurrentDate(addDays(currentDate, 7))
  }

  // Get user name by ID
  const getUserName = (userId: string) => {
    const user = mockUsers.find((u) => u.id === userId)
    return user ? user.name : "Unknown User"
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t("trainerView.calendar.title")}</h2>
        <Button onClick={() => setIsAddAppointmentOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("trainerView.calendar.addAppointment")}
        </Button>
      </div>

      {/* Calendar navigation */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <Button variant="outline" size="icon" onClick={previousWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <CardTitle>
              {format(weekStart, "MMMM d")} - {format(weekEnd, "MMMM d, yyyy")}
            </CardTitle>
            <Button variant="outline" size="icon" onClick={nextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day) => {
              const dayAppointments = trainerAppointments.filter((appointment) =>
                isSameDay(parseISO(appointment.date), day),
              )
              const isSelected = isSameDay(day, selectedDate)

              return (
                <div
                  key={day.toString()}
                  className={`p-2 text-center cursor-pointer rounded-md transition-colors ${
                    isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  }`}
                  onClick={() => setSelectedDate(day)}
                >
                  <div className="font-medium">{format(day, "EEE")}</div>
                  <div className="text-lg">{format(day, "d")}</div>
                  {dayAppointments.length > 0 && <Badge className="mt-1">{dayAppointments.length}</Badge>}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected day appointments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CalendarIcon className="mr-2 h-5 w-5" />
            {format(selectedDate, "EEEE, MMMM d, yyyy")}
          </CardTitle>
          <CardDescription>
            {selectedDateAppointments.length === 0
              ? t("trainerView.calendar.noAppointments")
              : t("trainerView.calendar.appointmentsCount", { count: selectedDateAppointments.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {selectedDateAppointments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {t("trainerView.calendar.noAppointmentsMessage")}
              </div>
            ) : (
              selectedDateAppointments
                .sort((a, b) => a.startTime.localeCompare(b.startTime))
                .map((appointment) => (
                  <div
                    key={appointment.id}
                    className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted"
                    onClick={() => setSelectedAppointment(appointment.id)}
                  >
                    <div>
                      <div className="font-medium">{getUserName(appointment.userId)}</div>
                      <div className="text-sm text-muted-foreground">
                        {appointment.startTime} - {appointment.endTime}
                      </div>
                    </div>
                    <Badge variant={appointment.status === "scheduled" ? "default" : "secondary"}>
                      {appointment.type}
                    </Badge>
                  </div>
                ))
            )}
          </div>
        </CardContent>
        <CardFooter className="justify-center border-t pt-4">
          <Button variant="outline" onClick={() => setIsAddAppointmentOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("trainerView.calendar.addAppointmentForDay", { day: format(selectedDate, "EEEE") })}
          </Button>
        </CardFooter>
      </Card>

      {/* Add appointment dialog */}
      <AddAppointmentDialog
        open={isAddAppointmentOpen}
        onOpenChange={setIsAddAppointmentOpen}
        selectedDate={selectedDate}
        trainerId={trainerId}
      />

      {/* Appointment details dialog */}
      {selectedAppointment && (
        <AppointmentDetails
          appointmentId={selectedAppointment}
          open={!!selectedAppointment}
          onOpenChange={(open) => !open && setSelectedAppointment(null)}
        />
      )}
    </div>
  )
}
